import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import WaveSurfer from 'wavesurfer.js';
import { 
  AppBar,
  Button,
  CssBaseline, 
  Box, 
  Dialog, 
  DialogContent,
  DialogTitle, 
  Fab, 
  Toolbar, 
  Typography,
  Snackbar,
  IconButton,
  Tooltip } from '@mui/material';
import { 
  Keyboard,
  Feedback,
  Description,
  Help
} from '@mui/icons-material';
import ApiTest from './ApiTest';
import './App.css';
import theme from './Themes';
import { ThemeProvider } from '@mui/material/styles';
import { AuditioBackendManager, AudioFile } from './AuditioBackend';
import YouTubeUploadAPI from './YouTubeUploadAPI';
import UploadTabs from './UploadTabs';
import WaveformAPI from './WaveformAPI';
import UnifiedSnippetsView from './UnifiedSnippetsView';
import AudioFileHandler from './AudioFileHandler';
import YouTubeVideoViewer from './YouTubeVideoViewer';
import RecentFilesView from './RecentFilesView';
import UserFilesView from './UserFilesView';
import LeftNavigation, { LeftNavToggleButton } from './LeftNavigation';
import { SessionManager } from './SessionManager';
import ConfirmationDialog from './components/ConfirmationDialog';
import Tutorial from './components/Tutorial';
import FileInfoSection from './components/FileInfoSection';
import WaveformLoadingIndicator from './components/WaveformLoadingIndicator';
import DriveShareDialog from './components/DriveShareDialog';
import { UploadLogs } from './components/UploadLogs';
import { LoggingProvider } from './contexts/LoggingContext';
import { LoggingConnector } from './components/LoggingConnector';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import UserProfile from './components/UserProfile';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import HomePage from './pages/HomePage';
import { GoogleDriveService } from './services/GoogleDriveService';


type WaveSurferInstance = ReturnType<typeof WaveSurfer.create>;

function AppContent() {
  const { user, firebaseUser } = useAuth();
  const [auditioBackendManager, setAuditioBackendManager] = useState<AuditioBackendManager>();
  const [isBackendInitialized, setIsBackendInitialized] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts
  const [openKeyboardShortcutsDialog, setOpenKeyboardShortcutsDialog] = useState(false);
  const [openTermsOfServiceDialog, setOpenTermsOfServiceDialog] = useState(false);
  const [audioFileIdFromUrl, setAudioFileIdFromUrl] = useState<number | null>(null);
  const [isRootUrl, setIsRootUrl] = useState(false);
  const [errorState, setErrorState] = useState<{id: number, message: string} | null>(null);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [showDeleteFileDialog, setShowDeleteFileDialog] = useState(false);
  const [isTutorialActive, setIsTutorialActive] = useState(false);
  const [showDriveShareDialog, setShowDriveShareDialog] = useState(false);
  const [driveFileData, setDriveFileData] = useState<{fileId: string, fileUrl?: string} | null>(null);

  const [audioFileId, setAudioFileId] = useState(0);
  const [audioFile, setAudioFile] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [renderTrigger, setRenderTrigger] = useState(0);
  const [leftNavOpen, setLeftNavOpen] = useState(false);
  const [showShareSnackbar, setShowShareSnackbar] = useState(false);
  const [ownerId, setOwnerId] = useState<number | null | undefined>(undefined);
  const [ownerDisplayName, setOwnerDisplayName] = useState<string | null>(null);
  const [waveformIsReady, setWaveformIsReady] = useState(false);
  const [waveformIsLoading, setWaveformIsLoading] = useState(false);
  const regions = useRef<{[id: number]: any}>({});
  const wavesurferRef = useRef<WaveSurferInstance | null>(null);
  const regionRef = useRef<any>(null);

  console.log('🚀 App component rendering - audioFileId:', audioFileId, 'pathname:', window.location.pathname);
  
  // Log when file info section should render
  useEffect(() => {
    if (audioFileId > 0) {
      console.log('🔍 File info section should render - audioFileId:', audioFileId);
    }
  }, [audioFileId]);

  // Force re-render when session data changes
  useEffect(() => {
    if (audioFileId > 0) {
      // Trigger a re-render when session data changes
      setRenderTrigger(prev => prev + 1);
    }
  }, [audioFileId]);

  // Fetch ownerId and ownerDisplayName when audioFileId changes
  useEffect(() => {
    const fetchOwnerInfo = async () => {
      if (audioFileId > 0 && auditioBackendManager) {
        try {
          const fileData = await auditioBackendManager.getAudioFileById(audioFileId);
          console.log('🔍 App.tsx - Fetched file data:', {
            audioFileId,
            ownerId: fileData?.ownerId,
            ownerIdType: typeof fileData?.ownerId,
            ownerDisplayName: fileData?.ownerDisplayName,
            allKeys: fileData ? Object.keys(fileData) : []
          });
          setOwnerId(fileData?.ownerId);
          setOwnerDisplayName(fileData?.ownerDisplayName || null);
        } catch (error) {
          console.error('Error fetching owner info:', error);
          setOwnerId(undefined);
          setOwnerDisplayName(null);
        }
      } else {
        setOwnerId(undefined);
        setOwnerDisplayName(null);
      }
    };
    fetchOwnerInfo();
  }, [audioFileId, auditioBackendManager]);

  // Update browser tab title when audio file changes
  useEffect(() => {
    if (audioFile && audioFile.trim() !== '') {
      // Update the title to show the track name
      // document.title = `${audioFile} - Auditio`;
      document.title = `Auditio - ${audioFile}`;
    } else {
      // Reset to default title when no audio file is loaded
      document.title = 'Auditio';
    }
  }, [audioFile]);

  useEffect(() => {
    const init = async () => {
      try {
        // Initialize session manager
        SessionManager.initializeSession();
        
        // Initialize backend manager
        if (!auditioBackendManager) {
          const backendManager = new AuditioBackendManager();
          setAuditioBackendManager(backendManager);
          
          // Start background deletion process for user files (non-blocking)
          // COMMENTED OUT: Background deletion disabled
          // console.log('🧹 Starting background deletion process...');
          // backendManager.deleteOldUserFiles().catch(error => {
          //   console.error('Background deletion process failed:', error);
          // });
        }
        
        setIsBackendInitialized(true);
        
        // Check if there's an audio file ID in the URL first
        const path = window.location.pathname;
        const audioFileMatch = path.match(/^\/(\d+)$/);
        
        if (!audioFileMatch) {
          // No audio file ID in URL - don't load any active file
          // This ensures the upload page is shown when visiting localhost:3000
          setAudioFileId(0);
          setAudioFile('');
        } else {
          // There is an audio file ID in URL - let the AudioFileHandler deal with it
          // Don't load the active file here, let the URL handler take precedence
          setAudioFileId(0);
          setAudioFile('');
        }
      } catch (error) {
        console.error("Initialization failed, retrying...", error);
        setRetryCount((prev) => prev + 1);
      }
    };

    if (!isBackendInitialized) {
      const retryTimeout = setTimeout(init, 1000);
      return () => clearTimeout(retryTimeout);
    }
  }, [retryCount, isBackendInitialized, auditioBackendManager]);

  // Handle URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      const path = window.location.pathname;
      const audioFileMatch = path.match(/^\/(\d+)$/);
      const apiTestMatch = path.match(/^\/api-test$/);
      const isApiRoute = path.startsWith('/api/');
      const userAgent = navigator.userAgent;
      
      // Diagnostic logging: Capture requests that reach React app
      console.log('🔍 [App] URL change detected:', {
        path,
        fullUrl: window.location.href,
        isNumericId: !!audioFileMatch,
        isApiRoute,
        isApiTest: !!apiTestMatch,
        userAgent: userAgent.substring(0, 100), // First 100 chars
        shouldBeHandledByApi: isApiRoute || (!!audioFileMatch && /bot|crawler|spider|facebookexternalhit|twitterbot|whatsapp|discordbot|applebot/i.test(userAgent))
      });
      
      // Log if this should have been handled by API function
      if (isApiRoute) {
        console.warn('⚠️ [App] API route reached React app! This should have been handled by serverless function:', path);
      } else if (audioFileMatch && /bot|crawler|spider|facebookexternalhit|twitterbot|whatsapp|discordbot|applebot/i.test(userAgent)) {
        console.warn('⚠️ [App] Crawler detected on numeric ID route! This should have been handled by preview function:', {
          path,
          userAgent: userAgent.substring(0, 100)
        });
      }
      
      if (apiTestMatch) {
        // API test route - don't set any audio file
        setAudioFileIdFromUrl(null);
        setIsRootUrl(false);
      } else if (audioFileMatch) {
        const fileId = parseInt(audioFileMatch[1]);
        setAudioFileIdFromUrl(fileId);
        setIsRootUrl(false);
      } else {
        setAudioFileIdFromUrl(null);
        setIsRootUrl(true);
      }
    };

    handleUrlChange();
    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  const handleAudioFileLoaded = async (audioFile: AudioFile) => {
    // First check if this file should be deleted due to expiration
    // COMMENTED OUT: File access deletion check disabled
    // if (auditioBackendManager) {
    //   console.log('🔍 Checking if file should be deleted due to expiration...');
    //   const wasDeleted = await auditioBackendManager.checkAndDeleteFileIfExpired(audioFile.id);
    //   if (wasDeleted) {
    //     console.log('🗑️ File was expired and deleted, redirecting to home page');
    //     // File was deleted, redirect to home page
    //     window.location.href = '/';
    //     return;
    //   }
    // }

    setAudioFileId(audioFile.id);
    setAudioFile(audioFile.fileName);
    setYoutubeUrl(audioFile.youtubeUrl || '');
    setRenderTrigger(prev => prev + 1);
    
    // Check if this file already exists in the session
    const recentFiles = SessionManager.getRecentFiles();
    const existingFile = recentFiles.find(file => file.id === audioFile.id);
    
    console.log('🔍 handleAudioFileLoaded:', {
      audioFileId: audioFile.id,
      fileName: audioFile.fileName,
      youtubeUrl: audioFile.youtubeUrl,
      existingFile: existingFile ? { id: existingFile.id, isUserUploaded: existingFile.isUserUploaded } : null,
      allFiles: recentFiles.map(f => ({ id: f.id, isUserUploaded: f.isUserUploaded }))
    });
    
    if (existingFile) {
      // File already exists in session - just update access time and ownerId if available
      console.log('🔍 File exists in session, updating access time');
      SessionManager.updateFileAccess(audioFile.id, audioFile.fileName, audioFile.ownerId);
    } else {
      // File doesn't exist in session - add it as user-uploaded by default
      // Only mark as shared if there's clear evidence (like a share token in URL)
      const isShared = window.location.pathname.includes('/shared/') || 
                      window.location.search.includes('share=') ||
                      window.location.search.includes('token=');
      
      console.log('🔍 File not found in session, adding as:', isShared ? 'shared' : 'user-uploaded');
      SessionManager.addRecentFile(audioFile.id, audioFile.fileName, !isShared, audioFile.uploadTime.getTime(), audioFile.ownerId);
    }
  };

  const handleAudioFileError = (errorMessage: string) => {
    // Extract the file ID from the error state for display
    const path = window.location.pathname;
    const audioFileMatch = path.match(/^\/(\d+)$/);
    if (audioFileMatch) {
      const fileId = parseInt(audioFileMatch[1]);
      
      // Check if this is a "no longer exists" error and remove from recent files
      if (errorMessage.includes('no longer exists')) {
        SessionManager.removeRecentFile(fileId);
        // Trigger a refresh of the UI
        setRenderTrigger(prev => prev + 1);
      }
      
      setErrorState({
        id: fileId,
        message: errorMessage
      });
    }
  };

  const handleFileSelect = (fileId: number) => {
    // Navigate to the selected file
    window.location.href = `/${fileId}`;
  };

  const handleUploadClick = () => {
    // Navigate to home page for upload
    window.location.href = '/';
  };

  const handleDeleteAllFiles = async () => {
    setShowDeleteAllDialog(true);
  };

  const confirmDeleteAllFiles = async () => {
    setShowDeleteAllDialog(false);
    if (auditioBackendManager && firebaseUser) {
      try {
        // Get Firebase ID token for authentication
        const idToken = await firebaseUser.getIdToken();
        
        // Get recent files and filter to only user-uploaded files
        const recentFiles = SessionManager.getRecentFiles();
        const userUploadedFiles = recentFiles.filter(file => file.isUserUploaded);
        
        // Delete only user-uploaded files from backend
        for (const file of userUploadedFiles) {
          try {
            await auditioBackendManager.deleteFile(file.id, idToken);
          } catch (err) {
            console.error('Error deleting file from backend:', file.id, err);
          }
        }
        
        // Remove only user-uploaded files from session
        for (const file of userUploadedFiles) {
          SessionManager.removeRecentFile(file.id);
        }
        
        // Clear current audio file if it was a user-uploaded file
        if (audioFileId > 0 && userUploadedFiles.some(f => f.id === audioFileId)) {
          setAudioFileId(0);
          setAudioFile('');
        }
        
        setRenderTrigger(prev => prev + 1);
        
        // Navigate back to home page after deletion
        setTimeout(() => window.location.href = '/', 100);
      } catch (err) {
        console.error('Error deleting user-uploaded files:', err);
      }
    }
  };

  const handleShare = async () => {
    // Check if this is a Google Drive file
    if (auditioBackendManager && audioFileId > 0) {
      try {
        const fileData = await auditioBackendManager.getAudioFileById(audioFileId);
        
        if (fileData?.driveFileId) {
          console.log('🔍 Google Drive file detected, checking permissions...');
          
          // Get Google Drive access token
          const driveService = new GoogleDriveService();
          let accessToken: string;
          try {
            accessToken = await driveService.getAccessToken();
          } catch (err) {
            console.error('❌ Failed to get Drive access token:', err);
            // If we can't get token, just copy the link (user can share manually)
            const fileUrl = `${window.location.origin}/${audioFileId}`;
            await navigator.clipboard.writeText(fileUrl);
            setShowShareSnackbar(true);
            return;
          }

          // Check if file is publicly accessible
          try {
            const response = await fetch(`${auditioBackendManager.getBaseUrl()}/drive/check-share`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                driveFileId: fileData.driveFileId,
                accessToken: accessToken
              })
            });

            if (response.ok) {
              const shareData = await response.json();
              
              // If not publicly accessible OR if we couldn't check permissions, show dialog
              if (!shareData.isPubliclyAccessible || !shareData.canShare) {
                // File is not public or we can't verify, show dialog to share
                setDriveFileData({
                  fileId: fileData.driveFileId,
                  fileUrl: shareData.fileUrl || fileData.driveFileUrl
                });
                setShowDriveShareDialog(true);
                return;
              }
            } else {
              // If check fails, show dialog anyway to be safe
              console.warn('⚠️ Permission check failed, showing share dialog');
              setDriveFileData({
                fileId: fileData.driveFileId,
                fileUrl: fileData.driveFileUrl
              });
              setShowDriveShareDialog(true);
              return;
            }
          } catch (err) {
            console.error('❌ Error checking file permissions:', err);
            // If check fails, show dialog anyway to be safe
            setDriveFileData({
              fileId: fileData.driveFileId,
              fileUrl: fileData.driveFileUrl
            });
            setShowDriveShareDialog(true);
            return;
          }
        }
      } catch (err) {
        console.error('❌ Error checking file data:', err);
        // Continue with normal share if check fails
      }
    }

    // Normal share flow (non-Drive file or already public Drive file)
    const fileUrl = `${window.location.origin}/${audioFileId}`;
    
    try {
      await navigator.clipboard.writeText(fileUrl);
      setShowShareSnackbar(true);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fileUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowShareSnackbar(true);
    }
  };

  const handleDriveShareAutomatically = async () => {
    if (!driveFileData || !auditioBackendManager) return;

    try {
      const driveService = new GoogleDriveService();
      const accessToken = await driveService.getAccessToken();

      const response = await fetch(`${auditioBackendManager.getBaseUrl()}/drive/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          driveFileId: driveFileData.fileId,
          accessToken: accessToken,
          makePublic: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.details || 'Failed to share file';
        
        // Check if it's the app authorization error
        if (errorMessage.includes('APP_NOT_AUTHORIZED')) {
          // Keep dialog open - it already explains the situation
          // Just copy the link
          const fileUrl = `${window.location.origin}/${audioFileId}`;
          await navigator.clipboard.writeText(fileUrl);
          setShowShareSnackbar(true);
          // Dialog stays open so user can click "Open in Google Drive"
          return;
        }
        
        // For other errors, show a snackbar message
        console.error('Failed to share file:', errorMessage);
        setShowShareSnackbar(true);
        // Keep dialog open so user can try opening in Drive
        return;
      }

      // File is now public, copy the Auditio link
      setShowDriveShareDialog(false);
      const fileUrl = `${window.location.origin}/${audioFileId}`;
      await navigator.clipboard.writeText(fileUrl);
      setShowShareSnackbar(true);
    } catch (err) {
      console.error('❌ Error sharing file:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      
      // Check if it's the app authorization error
      if (errorMessage.includes('APP_NOT_AUTHORIZED')) {
        // Keep dialog open - it already explains the situation
        // Just copy the link
        const fileUrl = `${window.location.origin}/${audioFileId}`;
        await navigator.clipboard.writeText(fileUrl);
        setShowShareSnackbar(true);
        // Dialog stays open so user can click "Open in Google Drive"
      } else {
        // For other errors, show a snackbar message
        console.error('Failed to share file:', errorMessage);
        setShowShareSnackbar(true);
        // Keep dialog open so user can try opening in Drive
      }
    }
  };

  const handleDriveShareOpenInDrive = () => {
    if (driveFileData?.fileUrl) {
      window.open(driveFileData.fileUrl, '_blank');
    }
    setShowDriveShareDialog(false);
    // Still copy the Auditio link
    const fileUrl = `${window.location.origin}/${audioFileId}`;
    navigator.clipboard.writeText(fileUrl).then(() => {
      setShowShareSnackbar(true);
    }).catch(() => {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = fileUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowShareSnackbar(true);
    });
  };

  const handleDeleteFile = async () => {
    setShowDeleteFileDialog(true);
  };

  const confirmDeleteFile = async () => {
    setShowDeleteFileDialog(false);
    if (auditioBackendManager && audioFileId > 0 && firebaseUser) {
      try {
        // Get Firebase ID token for authentication
        const idToken = await firebaseUser.getIdToken();
        await auditioBackendManager.deleteFile(audioFileId, idToken);
        setAudioFileId(0);
        setAudioFile('');
        setRenderTrigger(prev => prev + 1);
        // Navigate back to home page after deletion
        setTimeout(() => {
          window.location.href = '/';
        }, 100);
      } catch (err) {
        console.error('Error deleting file:', err);
      }
    }
  };


  // Show landing page when user is not logged in and on root URL
  // This replaces the AppBar, LeftNavigation, and main content with the landing page
  if (!user && isRootUrl) {
    return <HomePage />;
  }

  return (
    <>
      {/* Background pattern */}
      <div className="bg-pattern"></div>
      
      <Box 
        className="main-container"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        <AppBar
          position="static"
          elevation={0}
          sx={{ 
            color: 'white',
            flexShrink: 0
          }}
        >
          <Toolbar>
            <LeftNavToggleButton onClick={() => {
                setLeftNavOpen(true);
              }} />
            <Typography 
              variant="h5" 
              component="div" 
              sx={{
                fontFamily: 'Dancing Script, cursive',
                fontWeight: 700,
                fontSize: '30px',
                cursor: 'pointer',
                color: 'white',
                '&:hover': {
                  opacity: 0.8,
                },
                ml: 1,
              }}
              onClick={() => {
                window.location.href = '/';
              }}
            >
              <p style={{ margin: 10, fontFamily: 'Soria, serif', fontWeight: 400 }}>Auditio</p>
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            
            {/* User Profile Component */}
            <UserProfile />
            
            {/* Tutorial Help Button - only show when audio file is loaded */}
            {audioFileId > 0 && (
              <Tooltip title="Start Tutorial" placement="bottom">
                <IconButton
                  onClick={() => setIsTutorialActive(true)}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <Help />
                </IconButton>
              </Tooltip>
            )}
            
            {/* Upload Logs Button - only visible when showLogs is true */}
            {showLogs && <UploadLogs />}
          </Toolbar>
        </AppBar>

      {/* Left Navigation */}
      <LeftNavigation
        onFileSelect={handleFileSelect}
        onDeleteAllFiles={handleDeleteAllFiles}
        auditioBackendManager={auditioBackendManager}
        refreshTrigger={renderTrigger}
        isOpen={leftNavOpen}
        onToggle={setLeftNavOpen}
        showToggleButton={false}
        recentFilesCount={SessionManager.getRecentFiles().length}
        showLogs={showLogs}
        setShowLogs={setShowLogs}
      />

                <Box sx={{ 
                  ml: leftNavOpen ? { xs: 0, md: '400px' } : 0,
                  transition: 'margin-left 0.3s ease-in-out',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: 0
                }}>
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'auto'
          }}>
            {window.location.pathname === '/api-test' ? (
              <ApiTest />
            ) : errorState ? (
              // Show error state
              <AudioFileHandler
                audioFileId={errorState.id}
                onFileLoaded={handleAudioFileLoaded}
                onError={handleAudioFileError}
              />
            ) : audioFileIdFromUrl && audioFileId === 0 ? (
              // Show audio file handler when loading a file from URL
              <AudioFileHandler
                audioFileId={audioFileIdFromUrl}
                onFileLoaded={handleAudioFileLoaded}
                onError={handleAudioFileError}
              />
            ) : auditioBackendManager ? (
              <>
              {/* Home page layout - UploadTabs and UserFilesView side by side */}
              {audioFileId === 0 && (
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  alignItems="stretch"
                  justifyContent="flex-start"
                  gap="20px"
                  p={2}
                  sx={{ 
                    flex: 1,
                    minHeight: 0, // Allow content to shrink
                    width: '100%'
                  }}
                >
                  <Box sx={{ flex: { xs: 'none', md: '1 1 50%' }, minWidth: 0 }}>
                    <UploadTabs
                      auditioBackendManager={auditioBackendManager!}
                      audioFile={audioFile}
                      setAudioFile={setAudioFile}
                      audioFileId={audioFileId}
                      setAudioFileId={setAudioFileId}
                      renderTrigger={renderTrigger}
                      setRenderTrigger={setRenderTrigger}
                      forceNoFilesView={isRootUrl}
                      onFileSelect={handleFileSelect}
                    />
                  </Box>

                  {/* User Files View - Only show when authenticated and on home page */}
                  {user && isRootUrl && (
                    <Box sx={{ flex: { xs: 'none', md: '1 1 50%' }, minWidth: 0 }}>
                      <UserFilesView
                        auditioBackendManager={auditioBackendManager!}
                        onFileSelect={handleFileSelect}
                        refreshTrigger={renderTrigger}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* File viewer layout */}
              { audioFileId > 0 && (
                <>
                  {/* File info section - constrained width, centered */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="stretch"
                    justifyContent="flex-start"
                    p={2}
                    sx={{ 
                      width: '100%',
                      maxWidth: '1200px',
                      mx: 'auto',
                      boxSizing: 'border-box'
                    }}
                  >
                    <FileInfoSection
                      audioFileId={audioFileId}
                      audioFile={audioFile}
                      onShare={handleShare}
                      onDelete={handleDeleteFile}
                      isYouTube={!!youtubeUrl}
                      auditioBackendManager={auditioBackendManager}
                      onFileNameUpdate={(newFileName) => {
                        setAudioFile(newFileName);
                        setRenderTrigger(prev => prev + 1);
                      }}
                    />
                  </Box>

                  {/* Waveform/Player and Snippets - full width */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="stretch"
                    justifyContent="flex-start"
                    gap="20px"
                    sx={{ 
                      width: '100%',
                      boxSizing: 'border-box',
                      p: 2 // 16px padding (MUI spacing unit 2 = 16px)
                    }}
                  >
                    {(() => {
                      console.log('🔍 File display logic - youtubeUrl:', youtubeUrl, 'audioFileId:', audioFileId);
                      if (youtubeUrl) {
                        return (
                          // YouTube video viewer
                          <YouTubeVideoViewer
                            auditioBackendManager={auditioBackendManager!}
                            audioFileId={audioFileId}
                            youtubeUrl={youtubeUrl}
                            fileName={audioFile}
                            snippets={[]} // Snippets are loaded internally by YouTubeVideoViewer
                            onSnippetsChange={() => setRenderTrigger(prev => prev + 1)}
                          />
                        );
                      } else {
                        return (
                          // Audio waveform viewer
                          <>
                            <Box sx={{ position: 'relative', width: '100%' }}>
                              <WaveformLoadingIndicator
                                isLoading={waveformIsLoading}
                                isReady={waveformIsReady}
                                audioFileId={audioFileId}
                              />
                              <WaveformAPI
                                auditioBackendManager={auditioBackendManager!}
                                regions={regions}
                                wavesurferRef={wavesurferRef}
                                regionRef={regionRef}
                                renderTrigger={renderTrigger}
                                setRenderTrigger={setRenderTrigger}
                                audioFileId={audioFileId}
                                ownerId={ownerId}
                                onWaveformReadyChange={setWaveformIsReady}
                                onWaveformLoadingChange={setWaveformIsLoading}
                              />
                            </Box>

                            <UnifiedSnippetsView
                              auditioBackendManager={auditioBackendManager!}
                              regions={regions}
                              wavesurferRef={wavesurferRef}
                              regionRef={regionRef}
                              renderTrigger={renderTrigger}
                              setRenderTrigger={setRenderTrigger}
                              audioFileId={audioFileId}
                              waveformIsReady={waveformIsReady}
                              onSeek={(time) => {
                                if (wavesurferRef.current) {
                                  wavesurferRef.current.seekTo(time);
                                }
                              }}
                              onSnippetsChange={() => setRenderTrigger(prev => prev + 1)}
                              mode="audio"
                              ownerId={ownerId}
                              ownerDisplayName={ownerDisplayName}
                            />
                          </>
                        );
                      }
                    })()}
                  </Box>
                </>
              )}

              {/* Tutorial Component - only show when audio file is loaded */}
              {audioFileId > 0 && (
                <Tutorial 
                  isVisible={isTutorialActive}
                  onComplete={() => {
                    console.log('Tutorial completed');
                    setIsTutorialActive(false);
                  }}
                  isYouTube={!!youtubeUrl}
                />
              )}
              </>
            ) : (
              <p>Loading...</p>
            )}
          </Box>

          {/* Terms of Service and Privacy Policy Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem',
              padding: '1rem 0',
              borderTop: '1px solid rgba(0, 0, 0, 0.1)',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
            }}
          >
            <Typography
              variant="caption"
              component={Link}
              to="/privacy-policy"
              sx={{
                color: '#2c5aa0',
                fontSize: '0.75rem',
                fontWeight: 400,
                opacity: 0.8,
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  opacity: 1,
                  textDecoration: 'underline',
                }
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#2c5aa0',
                fontSize: '0.75rem',
                opacity: 0.5,
              }}
            >
              |
            </Typography>
            <Typography
              variant="caption"
              component={Link}
              to="/terms-of-service"
              sx={{
                color: '#2c5aa0',
                fontSize: '0.75rem',
                fontWeight: 400,
                opacity: 0.8,
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  opacity: 1,
                  textDecoration: 'underline',
                }
              }}
            >
              Terms of Service
            </Typography>
          </Box>
        </Box>

        <Fab
          className="keyboard-fab"
          onClick={() => setOpenKeyboardShortcutsDialog(true)}
          sx={{
            position: 'fixed',
            bottom: 16, // Distance from the bottom of the viewport
            left: 16,  // Distance from the right of the viewport
            background: 'linear-gradient(135deg, #fcb9aa, #f4a261) !important',
            boxShadow: '0 4px 12px rgba(252, 185, 170, 0.3) !important',
            '&:hover': {
              background: 'linear-gradient(135deg, #f4a261, #fcb9aa) !important',
              transform: 'scale(1.05)',
              boxShadow: '0 6px 16px rgba(252, 185, 170, 0.4) !important',
            }
          }}
        >
            <Keyboard></Keyboard>
        </Fab>

        <Dialog
          open={openKeyboardShortcutsDialog}
          onClose={() => setOpenKeyboardShortcutsDialog(false)}
        >
          <DialogTitle
            sx={{
              fontSize: '1.8em'
            }}
          >
            Keyboard Shortcuts
          </DialogTitle>
          <DialogContent>
              <strong>Space</strong>: Pause/Play audio<br /><br />
              <strong>Z</strong>: Rewind 30 seconds<br />
              <strong>X</strong>: Rewind 15 seconds<br />
              <strong>C</strong>: Rewind 5 seconds<br />
              <strong>V</strong>: Fast forward 5 seconds<br />
              <strong>B</strong>: Fast forward 15 seconds<br />
              <strong>N</strong>: Fast forward 30 seconds<br /><br />
              
              <strong>S</strong>: Create Start Marker<br />
              <strong>E</strong>: Create End Marker<br /><br />
              
              <strong>Touch Gestures:</strong><br />
              • Pinch to zoom (mobile/trackpad)<br />
              • Ctrl/Cmd + scroll to zoom (desktop)<br />
              • Two-finger pinch on Mac trackpad
          </DialogContent>
        </Dialog>

        {/* Terms of Service Dialog */}
        <Dialog
          open={openTermsOfServiceDialog}
          onClose={() => setOpenTermsOfServiceDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              fontSize: '1.8em',
              textAlign: 'center',
              color: '#333'
            }}
          >
            Terms of Service
          </DialogTitle>
          <DialogContent>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                fontSize: '1rem',
                color: '#555',
                textAlign: 'justify'
              }}
            >
              By using this website and uploading audio files, you acknowledge and agree that all uploaded content is the sole responsibility of the user who submitted it. The site owner and operators of this service do not claim ownership of, review, or verify the legality, accuracy, or integrity of any uploaded files. We are not liable for any copyright infringement, illegal content, or other legal issues arising from the files users choose to upload. You are solely responsible for ensuring that your use of this service complies with all applicable laws and regulations.
            </Typography>
          </DialogContent>
        </Dialog>

        <a href="https://forms.gle/C2jsBpdkhRMfVJh57" target="_blank" rel="noopener noreferrer">
          <Fab
            className="feedback-fab"
            sx={{
              position: 'fixed',
              bottom: 16, // Distance from the bottom of the viewport
              right: 16,  // Distance from the right of the viewport
              backgroundColor: 'rgba(56, 116, 203, 0.3)'
            }}
          >
              <Feedback></Feedback>
          </Fab>
        </a>



        {/* Share Success Snackbar */}
        <Snackbar
          open={showShareSnackbar}
          autoHideDuration={2000}
          onClose={() => setShowShareSnackbar(false)}
          message="Link copied to clipboard!"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />

        {/* Confirmation Dialog for Delete All Files */}
        <ConfirmationDialog
          open={showDeleteAllDialog}
          title="Delete All Uploaded Files?"
          message="Are you sure you want to delete all uploaded files? This action cannot be undone."
          confirmText="Delete All"
          severity="error"
          onConfirm={confirmDeleteAllFiles}
          onCancel={() => setShowDeleteAllDialog(false)}
        />

        {/* Confirmation Dialog for Delete Single File */}
        <ConfirmationDialog
          open={showDeleteFileDialog}
          title="Delete File?"
          message={`Are you sure you want to delete "${audioFile}"? This action cannot be undone.`}
          confirmText="Delete"
          severity="error"
          onConfirm={confirmDeleteFile}
          onCancel={() => setShowDeleteFileDialog(false)}
        />

        {/* Google Drive Share Dialog */}
        <DriveShareDialog
          open={showDriveShareDialog}
          onClose={() => {
            setShowDriveShareDialog(false);
            setDriveFileData(null);
          }}
          onOpenInDrive={handleDriveShareOpenInDrive}
          fileUrl={driveFileData?.fileUrl}
        />
      </Box>
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <LoggingProvider>
          <LoggingConnector>
            <ThemeProvider theme={theme}>
              <CssBaseline>
                <Routes>
                  {/* Authentication Routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  
                  {/* Legal Pages */}
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                  
                  {/* Main App Route */}
                  <Route path="/*" element={<AppContent />} />
                </Routes>
              </CssBaseline>
            </ThemeProvider>
          </LoggingConnector>
        </LoggingProvider>
      </AuthProvider>
    </Router>
  );
}

export default App; 
